import { useState } from "react";
import { Star, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

const BANNER_UNTIL = new Date("2026-08-08T23:59:59Z"); // ~15 days from launch
const DISMISS_KEY = "review-banner-dismissed";

export function ReviewBanner() {
  const [dismissed, setDismissed] = useState(() => localStorage.getItem(DISMISS_KEY) === "1");
  const [open, setOpen] = useState(false);
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState("");
  const [name, setName] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (dismissed || new Date() > BANNER_UNTIL) return null;

  const dismiss = () => { localStorage.setItem(DISMISS_KEY, "1"); setDismissed(true); };

  const submit = async () => {
    if (!rating) { toast.error("Please choose a star rating."); return; }
    setSubmitting(true);
    try {
      await api.post("/reviews", { rating, comment: comment.trim(), name: name.trim() });
      toast.success("Thank you for your review! 🙏");
      setOpen(false);
      dismiss();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not submit review.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <div data-testid="review-banner" className="bg-[#2C2A29] text-[#FAF9F6]">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 py-2.5 flex items-center justify-center gap-3 text-sm">
          <Star className="w-4 h-4 text-[#E4C97E]" fill="#E4C97E" />
          <span className="font-body">Enjoying It Is Established? We'd love your review.</span>
          <button data-testid="review-banner-open" onClick={() => setOpen(true)}
            className="underline underline-offset-2 hover:text-[#E4C97E] transition-colors duration-300 whitespace-nowrap">
            Leave a review
          </button>
          <button data-testid="review-banner-dismiss" onClick={dismiss} aria-label="Dismiss"
            className="ml-2 text-[#B7AE9F] hover:text-[#FAF9F6] transition-colors duration-300">
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {open && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" data-testid="review-modal">
          <div className="absolute inset-0 bg-black/40" onClick={() => setOpen(false)} />
          <div className="relative bg-[#FAF9F6] border border-[#D9D4CC] rounded-sm p-7 w-full max-w-md">
            <button onClick={() => setOpen(false)} className="absolute top-4 right-4 text-[#B7AE9F] hover:text-[#2C2A29]"><X className="w-5 h-5" /></button>
            <h2 className="font-heading text-2xl text-[#2C2A29] tracking-tight">Share your review</h2>
            <p className="font-decree text-base text-[#7A756E] mt-2">Your feedback helps us grow this ministry.</p>

            <div className="flex items-center gap-1.5 mt-5" data-testid="review-stars">
              {[1, 2, 3, 4, 5].map((n) => (
                <button key={n} data-testid={`review-star-${n}`} onClick={() => setRating(n)}
                  onMouseEnter={() => setHover(n)} onMouseLeave={() => setHover(0)} aria-label={`${n} stars`}>
                  <Star className="w-8 h-8 transition-transform duration-200 hover:scale-110"
                    fill={n <= (hover || rating) ? "#9B7E4E" : "none"} color={n <= (hover || rating) ? "#9B7E4E" : "#D9D4CC"} />
                </button>
              ))}
            </div>

            <textarea data-testid="review-comment" value={comment} onChange={(e) => setComment(e.target.value)} rows={4}
              placeholder="What do you love? What could be better?"
              className="mt-5 w-full bg-transparent border border-[#D9D4CC] px-4 py-3 text-base rounded-sm focus:ring-2 focus:ring-[#EAE6DF] focus:border-[#B7AE9F] focus:outline-none resize-none" />
            <input data-testid="review-name" value={name} onChange={(e) => setName(e.target.value)}
              placeholder="Your name (optional)"
              className="mt-3 w-full bg-transparent border border-[#D9D4CC] px-4 py-3 text-base rounded-sm focus:ring-2 focus:ring-[#EAE6DF] focus:border-[#B7AE9F] focus:outline-none" />

            <button data-testid="review-submit" onClick={submit} disabled={submitting}
              className="mt-6 w-full flex items-center justify-center gap-2 px-6 py-3 bg-[#2C2A29] text-[#FAF9F6] rounded-sm hover:opacity-90 transition-opacity duration-300 disabled:opacity-50">
              {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Submitting…</> : "Submit review"}
            </button>
          </div>
        </div>
      )}
    </>
  );
}
